/**
 * SuggestionRow — the redesigned anatomy of a pending Suggestion in
 * the Review surface. Replaces the legacy "buried 11px grey body
 * text + ungrouped list" pattern called out in the redesign brief.
 *
 * Anatomy (matches design/pages/desktop/PendingSuggestions.jsx):
 *   • Left: verb chip + avatar
 *   • Middle: target name + subtitle, tone-coded eyebrow, bullets
 *     (2-3 concrete evidence lines), "See N videos · why" disclosure
 *   • Right: verb-tinted primary action + snooze affordance
 *
 * The whole row is tappable — onSelect fires for clicks anywhere not
 * captured by a nested button. aria-expanded reflects whether this
 * row's evidence sheet is currently docked open.
 */
import React from 'react';
import {Box, Button, Stack, Typography} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {Suggestion, SuggestionVerb, Author, Video} from '../../types/generated/graphql';
import {VerbChip} from './VerbChip';
import {
  AVATAR_TONE_SX,
  AvatarTone,
  VERB_TONES,
  avatarToneForVerb,
  verbTokenForVerb,
} from './EvidenceSheet/tones';

export interface SuggestionRowProps {
  suggestion: Suggestion;
  selected: boolean;
  onSelect: () => void;
  onApply: () => void;
  onSnooze: () => void;
  applying?: boolean;
  snoozing?: boolean;
}

function targetDisplay(target: Suggestion['target']): {
  name: string;
  subtitle: string;
  kind: 'channel' | 'video';
  /** Real thumbnail URL when the resolver provided one. */
  thumbnailUrl: string | null;
} {
  // SuggestionTarget is a union of Author | Video. Discriminate on
  // the presence of `title` (Video) — same heuristic the resolver
  // uses in __resolveType.
  if (target && 'title' in (target as Record<string, unknown>)) {
    const v = target as Video;
    return {
      name: v.title ?? 'Untitled video',
      subtitle: v.author?.name ?? 'video',
      kind: 'video',
      // Prefer the video's own thumbnail; fall back to the channel
      // avatar so video rows always have an image.
      thumbnailUrl:
        v.thumbnails?.[0]?.url ?? v.author?.thumbnails?.[0]?.url ?? null,
    };
  }
  const a = target as Author;
  return {
    name: a.name ?? 'Channel',
    subtitle: 'channel',
    kind: 'channel',
    thumbnailUrl: a.thumbnails?.[0]?.url ?? null,
  };
}

function TargetThumbnail({
  thumbnailUrl,
  name,
  tone,
  kind,
}: {
  thumbnailUrl: string | null;
  name: string;
  tone: AvatarTone;
  kind: 'channel' | 'video';
}): React.JSX.Element {
  // Channel avatars are circular, video thumbnails are 16:9. Both fall
  // back to a tone-colored initial when the resolver didn't return
  // a thumbnail.
  const size = kind === 'channel' ? 36 : 56;
  const aspect = kind === 'channel' ? '1 / 1' : '16 / 9';
  const radius = kind === 'channel' ? '50%' : '6px';
  if (thumbnailUrl) {
    return (
      <Box
        component="img"
        src={thumbnailUrl}
        alt={name}
        sx={{
          width: kind === 'channel' ? size : 64,
          aspectRatio: aspect,
          borderRadius: radius,
          objectFit: 'cover',
          flexShrink: 0,
          backgroundColor: '#E2E8F0',
        }}
      />
    );
  }
  const initial = name?.[0]?.toUpperCase() ?? '?';
  const sx = AVATAR_TONE_SX[tone];
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: radius,
        background: sx.background,
        color: sx.color,
        width: kind === 'channel' ? size : 64,
        aspectRatio: aspect,
        fontSize: size * 0.42,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {initial}
    </Box>
  );
}

function actionLabelForVerb(verb: SuggestionVerb): string {
  switch (verb) {
    case SuggestionVerb.Block:
      return 'Block channel';
    case SuggestionVerb.Follow:
      return 'Follow channel';
    case SuggestionVerb.Boost:
      return 'Boost in feed';
    case SuggestionVerb.Skip:
    default:
      return 'Skip';
  }
}

export function SuggestionRow({
  suggestion,
  selected,
  onSelect,
  onApply,
  onSnooze,
  applying,
  snoozing,
}: SuggestionRowProps): React.JSX.Element {
  const verbToken = verbTokenForVerb(suggestion.verb);
  const tones = VERB_TONES[verbToken];
  const target = targetDisplay(suggestion.target);
  const avatarTone: AvatarTone = avatarToneForVerb(verbToken);
  // Eyebrow is the REASON. Falls back to em-dash when the producer
  // hasn't written one (TODO(eyebrow-bullets) in suggestions.ts).
  const reason =
    suggestion.eyebrow && suggestion.eyebrow.trim().length > 0
      ? suggestion.eyebrow
      : '—';
  const seeMore =
    suggestion.videoCount > 0
      ? `See the ${suggestion.videoCount} video${
          suggestion.videoCount === 1 ? '' : 's'
        } · why this was ${suggestion.verb === SuggestionVerb.Follow ? 'suggested' : 'flagged'}`
      : 'See why this was suggested';

  return (
    <Box
      component="button"
      onClick={onSelect}
      aria-expanded={selected}
      aria-label={`${actionLabelForVerb(suggestion.verb)} — ${target.name}`}
      sx={{
        width: '100%',
        textAlign: 'left',
        borderRadius: '12px',
        border: selected ? '1px solid #4F46E5' : '1px solid #E2E8F0',
        boxShadow: selected ? '0 0 0 2px rgba(79, 70, 229, 0.15)' : 'none',
        backgroundColor: '#FFFFFF',
        p: 2,
        cursor: 'pointer',
        transition: 'border-color 150ms, background-color 150ms, box-shadow 150ms',
        '&:hover': {
          borderColor: selected ? '#4F46E5' : '#CBD5E1',
          backgroundColor: '#F8FAFC',
        },
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={1.5}>
        <Stack alignItems="center" spacing={1} sx={{flexShrink: 0}}>
          <VerbChip verb={suggestion.verb} tense="imperative" />
          <TargetThumbnail
            thumbnailUrl={target.thumbnailUrl}
            name={target.name}
            tone={avatarTone}
            kind={target.kind}
          />
        </Stack>

        <Box sx={{minWidth: 0, flex: 1}}>
          <Stack direction="row" alignItems="baseline" spacing={1}>
            <Typography
              component="h3"
              sx={{
                fontWeight: 600,
                color: '#0F172A',
                fontSize: '15px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {target.name}
            </Typography>
            <Typography sx={{color: '#64748B', fontSize: '11px'}}>{target.subtitle}</Typography>
          </Stack>
          <Typography
            sx={{
              mt: '2px',
              fontWeight: reason === '—' ? 400 : 500,
              color: reason === '—' ? '#94A3B8' : tones.accent,
              fontSize: '12px',
            }}
          >
            {reason}
          </Typography>

          {suggestion.bullets.length > 0 && (
            <Stack
              component="ul"
              spacing={0.5}
              sx={{mt: 1, mb: 0, pl: 0, listStyle: 'none'}}
            >
              {suggestion.bullets.map((b, i) => (
                <Stack
                  key={i}
                  component="li"
                  direction="row"
                  alignItems="flex-start"
                  spacing={1}
                  sx={{color: '#334155', fontSize: '12.5px', lineHeight: 1.5}}
                >
                  <Box
                    sx={{
                      mt: '6px',
                      width: '4px',
                      height: '4px',
                      borderRadius: '999px',
                      backgroundColor: tones.bar,
                      flexShrink: 0,
                    }}
                  />
                  <span>{b}</span>
                </Stack>
              ))}
            </Stack>
          )}

          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{mt: 1.25, color: '#94A3B8', fontSize: '11.5px'}}
          >
            <ChevronRightIcon sx={{fontSize: 12}} />
            <span>{seeMore}</span>
          </Stack>
        </Box>

        <Stack alignItems="flex-end" spacing={1} sx={{flexShrink: 0}}>
          <Button
            size="small"
            variant="contained"
            disabled={applying}
            onClick={ev => {
              ev.stopPropagation();
              onApply();
            }}
            sx={{
              backgroundColor: tones.primaryBg,
              color: '#FFFFFF',
              fontSize: '12px',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {backgroundColor: tones.primaryBgHover},
            }}
          >
            {actionLabelForVerb(suggestion.verb)}
          </Button>
          <Button
            size="small"
            disabled={snoozing}
            onClick={ev => {
              ev.stopPropagation();
              onSnooze();
            }}
            sx={{
              color: '#64748B',
              fontSize: '11px',
              textTransform: 'none',
              minHeight: 'unset',
              p: 0,
              '&:hover': {color: '#334155', backgroundColor: 'transparent'},
            }}
          >
            Snooze 7d
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
